import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Button } from "@chakra-ui/react";
import { FaMicrophone } from "react-icons/fa6";
import { FaPlay, FaRedo } from "react-icons/fa";
import SoundWave from "./SoundWave";
import WhiteActivityButton from "./WhiteActivityButton";
import { useSingleToast } from "../../hooks/useSingleToast";
import { useAudioPreWarm } from "../../hooks/useAudioPreWarm";

type SpeakButtonProps = {
  onStateChange?: (state: "idle" | "recording" | "recorded") => void;
  onRecordingStarted?: () => void;
  onPlayRecording?: () => void;
  onReplayVideo?: () => void;
  onTryAgain?: () => void;
};

export interface SpeakButtonHandle {
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  resetRecording: () => void;
  playAudio: () => void;
  pauseAudio: () => void;
  seekStart: () => void;
}

const stateStyles = {
  idle: {
    bg: "white",
    color: "var(--color-blue)",
  },
  recording: {
    bg: "var(--color-red)",
    color: "white",
    border: "transparent",
    padding: "2px",
  },
  recorded: {
    bg: "white",
    color: "var(--color-blue)",
  },
};

const SpeakButton = forwardRef<SpeakButtonHandle, SpeakButtonProps>(
  (
    {
      onStateChange,
      onRecordingStarted,
      onPlayRecording,
      onReplayVideo,
      onTryAgain,
    },
    ref
  ) => {
    const [state, setState] = useState<"idle" | "recording" | "recorded">(
      "idle"
    );
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [isPreparing, setIsPreparing] = useState(false);
    const toast = useSingleToast();
    const { isPreWarmed, isPermissionGranted } = useAudioPreWarm();

    // Centralized error handler for microphone/speaker related errors
    const handleMicrophoneError = (
      error: Error | DOMException,
      shouldClearPermission = false
    ) => {
      console.error("Microphone error:", error);

      if (shouldClearPermission) {
        localStorage.removeItem("microphonePermission");
      }

      let title = "Microphone error";
      let description =
        "An unexpected error occurred while accessing the microphone.";

      // Handle specific error cases
      if (
        error.name === "NotFoundError" ||
        error.name === "DevicesNotFoundError"
      ) {
        title = "No microphone found";
        description = "Please connect a microphone to use this feature.";
      } else if (
        error.name === "NotAllowedError" ||
        error.name === "PermissionDeniedError"
      ) {
        title = "Microphone access denied";
        description =
          "Please allow microphone access in your app settings to use this feature.";
        if (!shouldClearPermission) {
          localStorage.setItem("microphonePermission", "denied");
        }
      } else if (error.name === "NotReadableError") {
        title = "Microphone in use";
        description =
          "Your microphone is being used by another application. Please close other apps and try again.";
      } else if (error.name === "OverconstrainedError") {
        title = "Microphone configuration error";
        description =
          "Your microphone doesn't meet the required specifications.";
      } else if (error.name === "TypeError") {
        title = "Browser compatibility issue";
        description =
          "Your browser doesn't support microphone access. Please try a different browser.";
      }

      toast({
        title,
        description,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    };

    useEffect(() => {
      onStateChange?.(state);
    }, [state, onStateChange]);

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const requestMicrophonePermission = async () => {
      try {
        // Check if we've already been granted permission (stored in localStorage)
        const storedPermission = localStorage.getItem("microphonePermission");

        if (storedPermission === "granted") {
          try {
            // Try to get the stream without showing permission dialog
            const stream = await navigator.mediaDevices.getUserMedia({
              audio: true,
            });
            return stream;
          } catch (streamError: unknown) {
            // If the stored permission is stale, clear it and continue to request permission
            localStorage.removeItem("microphonePermission");
            // Continue to the permission request below
          }
        }

        // Show a more immediate loading state if this is the first request
        if (!isPreWarmed && !isPermissionGranted) {
          setIsPreparing(true);
        }

        // Request permission
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        // Store the permission state
        localStorage.setItem("microphonePermission", "granted");

        return stream;
      } catch (err: unknown) {
        // Use centralized error handler
        if (err instanceof Error || err instanceof DOMException) {
          handleMicrophoneError(err);
        }
        return null;
      }
    };

    const startRecording = async () => {
      // Prevent multiple clicks while preparing
      if (isPreparing) return;

      setIsPreparing(true);

      const stream = await requestMicrophonePermission();
      if (!stream) {
        setIsPreparing(false);
        return;
      }

      onRecordingStarted?.();
      setState("recording");
      setIsPreparing(false);
      streamRef.current = stream;
      audioChunksRef.current = [];

      let mimeType = "audio/mp4";
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "audio/webm";
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = "audio/aac";
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = "";
          }
        }
      }

      const mediaRecorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined
      );
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: mimeType || "audio/webm",
        });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        setState("recorded");

        const audio = new Audio();
        audio.src = url;
        audio.controls = false;
        audio.preload = "auto";
        audio.muted = false;
        audio.volume = 1.0;

        audioRef.current = audio;
      };

      mediaRecorder.start();
    };

    const stopRecording = () => {
      if (mediaRecorderRef.current && state === "recording") {
        mediaRecorderRef.current.stop();

        // stop all tracks on the stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
      }
    };

    const playAudio = () => {
      if (audioRef.current) {
        audioRef.current.play();
      }
    };

    const resetRecording = () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      setAudioUrl(null);
      setState("idle");
      audioRef.current = null;
    };

    const pauseAudio = () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };

    const seekStart = () => {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
      }
    };

    useImperativeHandle(ref, () => ({
      startRecording,
      stopRecording,
      playAudio,
      resetRecording,
      pauseAudio,
      seekStart,
    }));

    return (
      <div className="h-28 flex flex-col items-center justify-start w-full">
        {state === "recording" && (
          <WhiteActivityButton
            onClick={stopRecording}
            {...stateStyles.recording}
          >
            <SoundWave />
            TAP TO STOP
            <SoundWave />
          </WhiteActivityButton>
        )}

        {state === "recorded" && (
          <div className="flex flex-col items-center justify-start w-full">
            <div className="flex gap-4 w-full pb-1">
              <WhiteActivityButton
                leftIcon={<FaPlay className="w-4 h-4" />}
                onClick={() => {
                  if (audioRef.current) {
                    audioRef.current.currentTime = 0;
                    audioRef.current.play();
                  } else if (audioUrl) {
                    // Fallback: recreate audio element if ref is missing
                    const newAudio = new Audio(audioUrl);
                    newAudio.volume = 1.0;
                    newAudio.muted = false;
                    audioRef.current = newAudio;
                    newAudio.play();
                  }
                  onPlayRecording?.();
                }}
                flex="1"
                width="auto"
              >
                Play recording
              </WhiteActivityButton>
              <WhiteActivityButton
                leftIcon={<FaRedo className="w-4 h-4" />}
                onClick={onReplayVideo}
                flex="1"
                width="auto"
              >
                Replay video
              </WhiteActivityButton>
            </div>
            <Button
              onClick={onTryAgain}
              variant="ghost"
              color="var(--color-grey)"
              fontSize="var(--base-font-size)"
              width="100%"
              _hover={{ bg: "transparent", color: "var(--color-orange)" }}
            >
              Try speaking again
            </Button>
          </div>
        )}

        {state === "idle" && (
          <WhiteActivityButton
            leftIcon={<FaMicrophone className="w-6 h-6" />}
            onClick={startRecording}
            disabled={isPreparing}
            {...stateStyles.idle}
          >
            {isPreparing ? "Loading..." : "TAP TO SPEAK"}
          </WhiteActivityButton>
        )}
      </div>
    );
  }
);

export default SpeakButton;
